import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon, IconName } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { PRODUCTS, Product, ProductTarget } from '../data/products';
import { generateUUID } from '../core/uuid';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M03HomeScreen'>;

const VN_WEEKDAYS = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];

const formatVietnameseDate = (date: Date): string => {
  const weekday = VN_WEEKDAYS[date.getDay()];
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${weekday}, ${dd}/${mm}/${yyyy}`;
};

interface Activity {
  id: string;
  customerName: string;
  age: number;
  productId: string;
  timeAgo: string;
}

const RECENT_ACTIVITIES: Activity[] = [
  { id: 'a1', customerName: 'Lê Văn Thành',   age: 32, productId: 'XPQ', timeAgo: '10 phút trước' },
  { id: 'a2', customerName: 'Nguyễn Thị Mai',  age: 28, productId: 'XTL', timeAgo: '2 giờ trước' },
  { id: 'a3', customerName: 'Trần Quang Hải',  age: 45, productId: 'XUM', timeAgo: 'Hôm qua' },
];

const PRODUCT_ICON: Record<ProductTarget, IconName> = {
  'life-protection': 'shield',
  'education':       'school',
  'retirement':      'elderly',
  'savings':         'savings',
};

const PRODUCT_ICON_COLOR: Record<ProductTarget, string> = {
  'life-protection': colors.primary,
  'education':       colors.tertiary,
  'retirement':      '#6F42C1',
  'savings':         colors.secondary,
};

const PRODUCT_TINT: Record<Product['type'], { bg: string; accent: string }> = {
  'linked-general': { bg: '#FFF1F2', accent: '#E11D48' },
  'linked-unit':    { bg: '#E6F0FA', accent: colors.primary },
};

interface StatCardProps {
  label: string;
  value: string;
  subLabel?: string;
  trend?: string;
  extra?: React.ReactNode;
  accent?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subLabel, trend, extra, accent = colors.primary }) => (
  <View style={statStyles.card}>
    <View style={statStyles.topRow}>
      <Text style={statStyles.label}>{label}</Text>
      {trend && (
        <View style={statStyles.trendPill}>
          <Icon name="trending_up" size={12} color={colors.secondary} />
          <Text style={statStyles.trendText}>{trend}</Text>
        </View>
      )}
    </View>
    <Text style={[statStyles.number, { color: accent }]}>{value}</Text>
    {subLabel && <Text style={statStyles.subLabel}>{subLabel}</Text>}
    {extra}
  </View>
);

const statStyles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    padding: spacing[4],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
    minWidth: 100,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[1],
  },
  label: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  trendPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: '#e6fbf0',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  trendText: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    color: colors.secondary,
  },
  number: {
    fontSize: 28,
    fontWeight: fontWeights.bold,
    letterSpacing: -0.5,
    marginVertical: spacing[1],
  },
  subLabel: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },
});

export const M03HomeScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const handleCreateNew = () => {
    const illustrationId = generateUUID();
    navigation.navigate('M06InputCustomerScreen', { illustrationId });
  };

  const handleProductTap = (product: Product) => {
    navigation.navigate('M05ProductDetailScreen', { productId: product.id });
  };

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      {/* Greeting */}
      <View style={[styles.header, isDesktop && styles.headerDesktop]}>
        <View style={isDesktop ? styles.greetingCenter : undefined}>
          <Text style={[styles.welcomeText, isDesktop && styles.welcomeTextDesktop]}>
            Xin chào, Anh Minh! 👋
          </Text>
          <Text style={[styles.dateText, isDesktop && styles.dateTextDesktop]}>
            {formatVietnameseDate(new Date())} — Đây là bảng điều khiển của bạn
          </Text>
        </View>
      </View>

      {/* Stats Cards — hidden
      <View style={[styles.statsRow, isDesktop && styles.statsRowDesktop]}>
        <StatCard
          label="Lượt minh họa"
          value="128"
          subLabel="tháng này"
          trend="+12%"
        />
        <StatCard
          label="KH đã tư vấn"
          value="45"
          subLabel="khách mới"
          trend="+5 mới"
          accent={colors.secondary}
        />
        <StatCard
          label="Tỷ lệ chốt"
          value="68%"
          accent={colors.tertiary}
          extra={
            <View style={styles.progressTrack}>
              <View style={[styles.progressFill, { width: '68%' }]} />
            </View>
          }
        />
      </View>
      */}

      {/* Reminder — only shown on mobile (on desktop it's in the header) */}
      {!isDesktop && (
        <TouchableOpacity style={styles.reminderCard} activeOpacity={0.85}>
          <View style={styles.reminderIconWrap}>
            <Icon name="notifications" size={20} color={colors.primary} />
            <View style={styles.reminderDot} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.reminderTitle}>Lời nhắc hôm nay</Text>
            <Text style={styles.reminderText} numberOfLines={2}>
              Bạn có 3 lịch hẹn tư vấn cần chuẩn bị.
            </Text>
          </View>
          <Text style={styles.reminderAction}>Xem thêm</Text>
        </TouchableOpacity>
      )}

      {/* Primary CTA — only shown on mobile (on desktop it's in the header) */}
      {!isDesktop && (
        <TouchableOpacity style={styles.createBtn} onPress={handleCreateNew} activeOpacity={0.9}>
          <Icon name="add" size={22} color="#ffffff" />
          <Text style={styles.createBtnText}>Tạo minh họa mới</Text>
        </TouchableOpacity>
      )}

      {/* Quick Actions */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Hành động nhanh</Text>
      </View>

      <View style={[styles.quickActionsRow, isDesktop && styles.quickActionsRowDesktop]}>
        <TouchableOpacity
          style={[styles.quickActionCard, { borderLeftColor: colors.primary }]}
          activeOpacity={0.85}
          onPress={handleCreateNew}
        >
          <View style={[styles.quickActionIcon, { backgroundColor: '#E6F8EE' }]}>
            <Icon name="calculate" size={26} color={colors.primary} />
          </View>
          <View style={styles.quickActionBody}>
            <Text style={styles.quickActionTitle}>Tạo minh họa</Text>
            <Text style={styles.quickActionDesc} numberOfLines={2}>
              Tạo bảng ước tính dòng tiền mới cho khách hàng
            </Text>
          </View>
          <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.quickActionCard, { borderLeftColor: '#3B82F6' }]}
          activeOpacity={0.85}
          onPress={() => navigation.navigate('M15CustomerListScreen')}
        >
          <View style={[styles.quickActionIcon, { backgroundColor: '#EFF6FF' }]}>
            <Icon name="groups" size={26} color="#3B82F6" />
          </View>
          <View style={styles.quickActionBody}>
            <Text style={styles.quickActionTitle}>Quản lý khách hàng</Text>
            <Text style={styles.quickActionDesc} numberOfLines={2}>
              Xem, chỉnh sửa và tạo hồ sơ khách hàng
            </Text>
          </View>
          <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.quickActionCard, { borderLeftColor: '#F59E0B' }]}
          activeOpacity={0.85}
          onPress={() => navigation.navigate('M11HistoryScreen')}
        >
          <View style={[styles.quickActionIcon, { backgroundColor: '#FEF3C7' }]}>
            <Icon name="description" size={26} color="#F59E0B" />
          </View>
          <View style={styles.quickActionBody}>
            <Text style={styles.quickActionTitle}>Quản lý minh họa</Text>
            <Text style={styles.quickActionDesc} numberOfLines={2}>
              Danh sách tài liệu minh họa đã tạo
            </Text>
          </View>
          <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
        </TouchableOpacity>
      </View>

      {/* Recent Activities — hidden temporarily */}
      {/*
      <Text style={styles.sectionTitleActivities}>Hoạt động gần đây</Text>
      <View style={styles.activitiesList}>
        {RECENT_ACTIVITIES.map((act, idx) => {
          const product = PRODUCTS.find((p) => p.id === act.productId);
          const tint = product ? PRODUCT_TINT[product.type] : { bg: '#f0f2f5', accent: colors.onSurfaceVariant };
          return (
            <TouchableOpacity
              key={act.id}
              style={[
                styles.activityItem,
                idx === RECENT_ACTIVITIES.length - 1 && { borderBottomWidth: 0 },
              ]}
              activeOpacity={0.85}
              onPress={() => product && navigation.navigate('M07ResultScreen', { illustrationId: act.id })}
            >
              <View style={[styles.activityIconWrap, { backgroundColor: tint.bg }]}>
                <Icon name="description" size={20} color={tint.accent} />
              </View>
              <View style={styles.activityInfo}>
                <Text style={styles.activityTitle} numberOfLines={1}>
                  {act.customerName}, {act.age} tuổi
                </Text>
                <Text style={styles.activitySub} numberOfLines={1}>
                  Sản phẩm: {product?.name ?? act.productId}
                </Text>
              </View>
              <Text style={styles.activityTime}>{act.timeAgo}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      */}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: spacing[4],
    paddingTop: spacing[4],
    paddingBottom: spacing[8],
    backgroundColor: '#f5f7fa',
    flexGrow: 1,
  },
  /* Greeting */
  header: {
    marginBottom: spacing[4],
  },
  headerDesktop: {
    alignItems: 'center',
    paddingVertical: spacing[6],
    marginBottom: spacing[5],
  },
  greetingCenter: {
    alignItems: 'center',
  },
  welcomeText: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  welcomeTextDesktop: {
    fontSize: 30,
    textAlign: 'center',
  },
  dateText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  dateTextDesktop: {
    fontSize: 15,
    textAlign: 'center',
    marginTop: spacing[2],
  },
  /* Stats row */
  statsRow: {
    flexDirection: 'column',
    gap: spacing[3],
    marginBottom: spacing[4],
  },
  statsRowDesktop: {
    flexDirection: 'row',
  },
  /* Progress bar (used in stat card) */
  progressTrack: {
    height: 6,
    backgroundColor: '#f0f2f5',
    borderRadius: 3,
    marginTop: spacing[2],
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.tertiary,
    borderRadius: 3,
  },
  /* Reminder (mobile only) */
  reminderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EDF1F5',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    marginBottom: spacing[4],
    gap: spacing[3],
  },
  reminderIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  reminderDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.tertiary,
    borderWidth: 1.5,
    borderColor: '#EDF1F5',
  },
  reminderTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  reminderText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
    lineHeight: 16,
  },
  reminderAction: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  /* CTA button (mobile only) */
  createBtn: {
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[4],
    borderRadius: borderRadius.md,
    gap: spacing[2],
    marginBottom: spacing[5],
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
  createBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.3,
  },
  /* Products */
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: spacing[3],
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  seeAllText: {
    fontSize: 13,
    color: colors.secondary,
    fontWeight: fontWeights.semibold,
  },
  popularRow: {
    gap: spacing[3],
    paddingBottom: spacing[4],
    paddingRight: spacing[4],
  },
  productCard: {
    width: 168,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  productBanner: {
    height: 80,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  hotBadge: {
    position: 'absolute',
    top: spacing[2],
    right: spacing[2],
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 9999,
    backgroundColor: '#E11D48',
    gap: 2,
  },
  hotBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  productInfo: {
    padding: spacing[3],
    gap: 4,
  },
  productName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  productDesc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    lineHeight: 14,
  },
  productTargetsRow: {
    flexDirection: 'row',
    gap: 4,
    marginTop: spacing[1],
  },
  targetDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Quick Actions */
  quickActionsRow: {
    flexDirection: 'column',
    gap: spacing[3],
    marginBottom: spacing[4],
  },
  quickActionsRowDesktop: {
    flexDirection: 'row',
  },
  quickActionCard: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  quickActionIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActionBody: {
    flex: 1,
    gap: 2,
  },
  quickActionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  quickActionDesc: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    lineHeight: 16,
  },
  /* Activities */
  sectionTitleActivities: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginTop: spacing[2],
    marginBottom: spacing[3],
  },
  activitiesList: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 1,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing[4],
    gap: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  activityIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityInfo: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  activitySub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  activityTime: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
});
